/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import parameters from './__generated__/BusinessSummarySpaAppsQuery$parameters.ts';
import type { BusinessSummarySpaAppsQuery } from './__generated__/BusinessSummarySpaAppsQuery.graphql.ts';

type SummaryBusinessEntrypointComponent = EntryPointComponent<
	{
		summaryQuery: BusinessSummarySpaAppsQuery;
	},
	{},
	{},
	{}
>;

export const summaryBusinessEntrypoint: EntryPoint<
	SummaryBusinessEntrypointComponent,
	EntryPointRouteParams
> = createEntryPoint({
	root: JSResourceForUserVisible<SummaryBusinessEntrypointComponent>(
		/* webpackChunkName: "async-summary-business-entrypoint" */ './BusinessSummarySpaApps.tsx',
		{
			entryFsSsrLiftImportToModule: true,
		},
	),
	getPreloadProps: ({ context, tenantContext: { cloudId } }: EntryPointRouteParams) => {
		const projectKey = context.match.params.projectKey;

		if (projectKey == null) {
			throw new Error('Project key missing');
		}

		return {
			queries: {
				summaryQuery: {
					options: {
						fetchPolicy: 'store-and-network' as const,
						networkCacheConfig: {
							metadata: { META_SLOW_ENDPOINT: true },
						},
					},
					parameters,
					variables: {
						cloudId,
						projectKey,
						scope: { project: { projectKey } },
						isFilterRefinementEnabled: expValEquals(
							'jira_add_harmonisation_filter_to_summary_view',
							'isEnabled',
							true,
							false,
						),
						isAgenticSummaryViewEnabled: expValEquals(
							'jira_agentic_summary_view',
							'isEnabled',
							true,
							false,
						),
						pulseIntakeIssueSearchInput: {
							jql: `project = "${projectKey}" AND hierarchyLevel = 0 AND created >= -7d`,
						},
						pulseTriageIssueSearchInput: {
							jql: `project = "${projectKey}" AND hierarchyLevel = 0 AND statusCategory = "To Do"`,
						},
						pulseExecuteIssueSearchInput: {
							jql: `project = "${projectKey}" AND hierarchyLevel = 0 AND statusCategory = "In Progress"`,
						},
						pulseDeliveryIssueSearchInput: {
							jql: `project = "${projectKey}" AND hierarchyLevel = 0 AND statusCategory = "Done" AND statusCategoryChangedDate >= -7d`,
						},
						boundedProjectIssueSearchInput: {
							jql: `project = "${projectKey}" AND hierarchyLevel = 0`,
						},
						pulseIntakeBadgeIssueSearchInput: {
							jql: `project = "${projectKey}" AND statusCategory = "To Do" AND sprint is EMPTY AND (description is EMPTY OR updated <= "-12w")`,
						},
						pulseTriageBadgePrimaryIssueSearchInput: {
							jql: `project = "${projectKey}" AND resolution is EMPTY AND (due < now() OR (sprint is EMPTY AND due <= endOfDay("+14d")) OR (sprint is not EMPTY AND due <= endOfDay("+7d")))`,
						},
						pulseTriageBadgeFallbackIssueSearchInput: {
							jql: `project = "${projectKey}" AND statusCategory = "To Do" AND description is not EMPTY AND (sprint is EMPTY OR due is EMPTY)`,
						},
						pulseExecuteBadgePrimaryIssueSearchInput: {
							jql: `project = "${projectKey}" AND (deploymentState = "failed" OR buildState = "failed")`,
						},
						pulseExecuteBadgeFallbackIssueSearchInput: {
							jql: `project = "${projectKey}" AND "Agent Sessions"[state] = "AWAITING_INPUT" AND statusCategory = "In Progress"`,
						},
						pulseDeliveryBadgePrimaryIssueSearchInput: {
							jql: `project = "${projectKey}" AND resolution is EMPTY AND (issueLinkType in ("is blocked by", "Blocks") OR flagged = Impediment OR (sprint in openSprints() AND status = "In Progress" AND updated <= "-7d") OR (sprint is EMPTY AND statusCategory = "In Progress" AND updated <= "-14d"))`,
						},
						pulseDeliveryBadgeFallbackIssueSearchInput: {
							jql: `project = "${projectKey}" AND (statusCategory = "In Progress" OR assignee is not EMPTY OR sprint is not EMPTY) AND updated <= "-7d"`,
						},
					},
				},
			},
		};
	},
});
